import { SupportedLocale } from './supported-locales';

/**
 * Locales the on-prem Keycloak realm advertises via `supportedLocales`
 * (see ml-scribe `bootstrap/keycloak/realm-onprem.json`). These are the codes
 * the `heidi` login theme's `keycloak.v2` parent ships translations for.
 * Anything outside this set makes Keycloak fall back to its `defaultLocale`.
 */
const KEYCLOAK_SUPPORTED_LOCALES: ReadonlySet<string> = new Set([
  'en',
  'ar',
  'ca',
  'da',
  'de',
  'es',
  'fi',
  'fr',
  'it',
  'ja',
  'ko',
  'nl',
  'no',
  'pl',
  'pt-BR',
  'sv',
  'zh-CN',
  'zh-TW',
]);

/**
 * Maps an in-app {@link SupportedLocale} (BCP-47, e.g. `fr-FR`) to a single
 * value for Keycloak's `ui_locales` authorize param, so the Keycloak login
 * page renders in the same language the app resolved.
 *
 * Resolution order:
 *   1. The exact input if the realm supports it (e.g. `zh-CN`, `zh-TW`).
 *   2. The language subtag if the realm supports it (e.g. `fr-FR` -> `fr`).
 *   3. `en` as final fallback.
 *
 * Examples:
 *   `zh-CN`       -> `zh-CN`
 *   `fr-FR`       -> `fr`
 *   `nb-NO`       -> `no`     (realm uses the `no` language tag for Bokmål)
 *   `pt-PT`       -> `pt-BR`  (realm ships pt-BR, the only Portuguese variant)
 *   `en-psaccent` -> `en`     (dev-only pseudolocale)
 */
export const toKeycloakUiLocales = (locale: SupportedLocale | string): string => {
  if ((locale as SupportedLocale) === SupportedLocale.EnPs) {
    return SupportedLocale.En;
  }

  // App locale nb-NO (Bokmål); the realm advertises the `no` language tag.
  if ((locale as SupportedLocale) === SupportedLocale.NbNo) {
    return 'no';
  }

  // App locale pt-PT; the realm ships only pt-BR translations.
  if ((locale as SupportedLocale) === SupportedLocale.PtPt) {
    return 'pt-BR';
  }

  if (KEYCLOAK_SUPPORTED_LOCALES.has(locale)) {
    return locale;
  }

  const language = locale.split('-')[0];
  if (language && KEYCLOAK_SUPPORTED_LOCALES.has(language)) {
    return language;
  }

  return SupportedLocale.En;
};
